import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Modal from './Modal';
import { apiClient, AdminResponse, UserUpdateRequest } from '../lib/api';

interface EditUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  user: AdminResponse | null;
  onSuccess?: () => void;
  onError?: (message: string) => void;
}

const EditUserModal: React.FC<EditUserModalProps> = ({
  isOpen,
  onClose,
  user,
  onSuccess,
  onError,
}) => {
  const [role, setRole] = useState('admin');
  const [error, setError] = useState('');
  const queryClient = useQueryClient();

  useEffect(() => {
    if (user && isOpen) {
      setRole(user.role);
      setError('');
    }
  }, [user, isOpen]);

  const updateUserMutation = useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: UserUpdateRequest }) =>
      apiClient.updateUser(userId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      onClose();
      setError('');
      onSuccess?.();
    },
    onError: (err: Error) => {
      const message = err.message || 'Failed to update user';
      setError(message);
      onError?.(message);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    updateUserMutation.mutate({
      userId: user.id,
      data: { role }
    });
  };

  const handleClose = () => {
    if (!updateUserMutation.isLoading) {
      onClose();
      setError('');
    }
  };

  const footer = (
    <>
      <button
        type="button"
        className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
        onClick={handleClose}
        disabled={updateUserMutation.isLoading}
      >
        Cancel
      </button>
      <button
        type="submit"
        form="edit-user-form"
        className="inline-flex w-full justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        disabled={updateUserMutation.isLoading}
      >
        {updateUserMutation.isLoading ? 'Updating...' : 'Update User'}
      </button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit User"
      footer={footer}
      size="md"
    >
      <form id="edit-user-form" onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700">Email</label>
          <input
            type="text"
            value={user?.email || ''}
            className="mt-1 block w-full rounded-md border border-gray-300 bg-gray-50 shadow-sm sm:text-sm px-3 py-2"
            disabled
            readOnly
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Tenant</label>
          <input
            type="text"
            value={user?.tenant_name && user?.tenant_id
              ? `${user.tenant_name} (${user.tenant_id.substring(0, 8)}...)`
              : 'System Admin'}
            className="mt-1 block w-full rounded-md border border-gray-300 bg-gray-50 shadow-sm sm:text-sm px-3 py-2"
            disabled
            readOnly
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Status</label>
          <input
            type="text"
            value={user?.is_active ? 'Active' : 'Inactive'}
            className="mt-1 block w-full rounded-md border border-gray-300 bg-gray-50 shadow-sm sm:text-sm px-3 py-2"
            disabled
            readOnly
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Created At</label>
          <input
            type="text"
            value={user ? new Date(user.created_at).toLocaleDateString() : ''}
            className="mt-1 block w-full rounded-md border border-gray-300 bg-gray-50 shadow-sm sm:text-sm px-3 py-2"
            disabled
            readOnly
          />
        </div>

        <div>
          <label htmlFor="user-role" className="block text-sm font-medium text-gray-700">
            Role *
          </label>
          <select
            id="user-role"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="mt-1 block w-full rounded-md border border-gray-300 shadow-sm focus:outline-none focus:border-gray-400 sm:text-sm px-3 py-2"
            disabled={updateUserMutation.isLoading}
          >
            <option value="admin">Admin</option>
            <option value="manager">Manager</option>
            <option value="user">User</option>
          </select>
        </div>
      </form>
    </Modal>
  );
};

export default EditUserModal;
